import Swal from "sweetalert2";
import { changeUserStatus } from "../Services/User";
import { Success, Error } from "../Components/Modal";
import { formatBookingCode } from "./bookingCode";
import { adminSwalOptions as swalTheme } from "./adminSwal";

const ACTION_LABELS = {
  after_ride: "Let the current delivery finish, then suspend automatically",
  cancel_ride: "Cancel the delivery now and suspend immediately (payment hold is released)",
  reassign_ride: "Remove this driver from the delivery, find a new driver, and suspend immediately",
  cancel_search: "Stop the search, release the payment hold, and suspend now",
};

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[ch]);

const personName = (p) =>
  p ? `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.email || "-" : "-";

const rideSummaryHtml = (ride) => {
  if (!ride) return "";
  const pickup = ride?.bookingDetails?.pickupLocation?.name;
  const drop = ride?.bookingDetails?.deliveryLocation?.name || ride?.bookingDetails?.dropLocation?.name;
  return `
    <div class="admin-swal-summary">
      <div><b>Booking ID:</b> ${escapeHtml(formatBookingCode(ride))}</div>
      <div><b>Status:</b> ${escapeHtml(ride.status)}</div>
      <div><b>User:</b> ${escapeHtml(personName(ride.user))}</div>
      <div><b>Driver:</b> ${escapeHtml(personName(ride.driver))}</div>
      ${pickup ? `<div><b>Pickup:</b> ${escapeHtml(pickup)}</div>` : ""}
      ${drop ? `<div><b>Drop:</b> ${escapeHtml(drop)}</div>` : ""}
    </div>`;
};

const askReason = async (title) => {
  const result = await Swal.fire({
    ...swalTheme,
    title,
    input: "textarea",
    inputLabel: "Reason (shared with the account holder)",
    inputPlaceholder: "Optional",
    showCancelButton: true,
    confirmButtonText: "Continue",
    cancelButtonText: "Cancel",
  });
  return result.isConfirmed ? (result.value || "").trim() : null;
};

const finish = (res, title, onDone) => {
  if (onDone) onDone(res);
  Success(title, res?.data?.message);
};

const fail = (err) => Error(err?.response?.data?.message || err?.message);

/**
 * Activate / suspend a user, driver or business, handling the case where
 * the account is in the middle of a delivery.
 *
 * @param {object} params
 * @param {object} params.item     user doc from the list (needs _id, active, pendingSuspension, firstName)
 * @param {string} params.label    "Driver" | "User" | "Business" (used in titles)
 * @param {Function} [params.onDone] called after a successful change (e.g. refetch)
 * @param {Function} [params.setBusy]
 */
export const runAccountStatusChange = async ({ item, label = "User", onDone, setBusy }) => {
  const name = item?.firstName || `this ${label.toLowerCase()}`;
  const title = `${label} Status`;
  const call = async (body) => {
    setBusy?.(true);
    try {
      return await changeUserStatus(item._id, body);
    } finally {
      setBusy?.(false);
    }
  };

  try {
    if (item?.active && item?.pendingSuspension) {
      const result = await Swal.fire({
        ...swalTheme,
        icon: "info",
        title: `${name} is scheduled for suspension`,
        text: "The account will be suspended automatically when the current delivery ends.",
        showCancelButton: true,
        confirmButtonText: "Undo scheduled suspension",
        cancelButtonText: "Close",
      });
      if (!result.isConfirmed) return;
      finish(await call({ action: "undo_scheduled" }), title, onDone);
      return;
    }

    if (!item?.active) {
      const result = await Swal.fire({
        ...swalTheme,
        icon: "question",
        title: `Are you sure you want to make ${name} active?`,
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });
      if (!result.isConfirmed) return;
      finish(await call({}), title, onDone);
      return;
    }

    const reason = await askReason(`Suspend ${name}?`);
    if (reason === null) return;

    try {
      finish(await call({ reason }), title, onDone);
    } catch (err) {
      const payload = err?.response?.data;
      if (err?.response?.status !== 409 || payload?.code !== "ACTIVE_RIDE") throw err;

      const allowed = payload?.data?.allowedActions || ["after_ride", "cancel_ride"];
      const inputOptions = allowed.reduce((acc, key) => {
        acc[key] = ACTION_LABELS[key] || key;
        return acc;
      }, {});

      const searchOnly = allowed.length === 1 && allowed[0] === "cancel_search";
      const choice = await Swal.fire({
        ...swalTheme,
        icon: "warning",
        title: searchOnly ? "Driver search in progress" : "Active delivery in progress",
        html: `<p class="admin-swal-message">${escapeHtml(payload?.message)}</p>${rideSummaryHtml(payload?.data?.ride)}`,
        input: "radio",
        inputOptions,
        inputValue: allowed[0],
        showCancelButton: true,
        confirmButtonText: "Apply",
        cancelButtonText: "Cancel",
        inputValidator: (value) => (!value ? "Choose what to do with the delivery" : undefined),
      });
      if (!choice.isConfirmed) return;
      finish(await call({ action: choice.value, reason }), title, onDone);
    }
  } catch (err) {
    fail(err);
  }
};
